import React, { useState, useEffect } from "react";
import {
  Input,
  DatePicker,
  Button,
  Typography,
  Select,
  Tooltip,
  Popconfirm,
} from "antd";
import dayjs from "dayjs";
import moment from "moment";
import { DeleteFilled } from "@ant-design/icons";

const { Text } = Typography;

function Submittal({
  submittal,
  workingDays,
  nonWorkingDays,
  scheduleIDsList,
  scheduleOptions,
  onChange,
  onDelete,
  deliveryStatus,
}) {
  const [data, setData] = useState(submittal);
  const [hasChanges, setHasChanges] = useState(false);

  const timeFormat = "MM-DD-YYYY";

  useEffect(() => {
    setData(submittal);
  }, [submittal]);

  // Check if this is a new item (not saved to backend yet)
  const isNewItem = data?.entryID?.toString().startsWith("new-");

  const handleWorkingDays = () => {
    // Convert object keys to an array
    const daysOfWeek = Object.keys(workingDays);

    // Move the first day to the end of the array
    const adjustingDayOffs = daysOfWeek.shift();
    daysOfWeek.push(adjustingDayOffs);

    // Convert the array back to an object
    const adjustedWorkingDays = daysOfWeek.reduce((acc, day, index) => {
      acc[day] = workingDays[day];
      return acc;
    }, {});
    return adjustedWorkingDays;
  };
  const handleNonWorkingDays = () => {
    let disabledDatesArray = [];

    Object.keys(nonWorkingDays).forEach((key) => {
      const holiday = nonWorkingDays[key];
      var currDate = moment(holiday["startDate"], "MM/DD/YYYY").startOf("day");
      var lastDate = moment(holiday["finishDate"], "MM/DD/YYYY").startOf("day");

      disabledDatesArray.push(moment(currDate).format(timeFormat));
      while (currDate.add(1, "days").diff(lastDate) < 0) {
        disabledDatesArray.push(moment(currDate).format(timeFormat));
      }
      disabledDatesArray.push(moment(lastDate).format(timeFormat));
    });

    let filteredDatesArray = [...new Set(disabledDatesArray)];
    return filteredDatesArray;
  };
  const handlePreviousAvailDate = (date) => {
    let weekends = handleWorkingDays();
    const daysOfWeek = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const dayName = daysOfWeek[new Date(date).getDay()];

    // let weekendIndex = new Date(date).getDay();
    let nonWorkingDaysArr = handleNonWorkingDays();
    let previousDate = moment(date)
      .subtract(1, "days")
      .startOf("day")
      .format(timeFormat);

    if (nonWorkingDaysArr.includes(date) || weekends[dayName] == "0") {
      while (nonWorkingDaysArr.includes(previousDate)) {
        previousDate = moment(previousDate)
          .subtract(1, "days")
          .startOf("day")
          .format(timeFormat);
      }
      while (weekends[new Date(previousDate).getDay()] == "0") {
        previousDate = moment(previousDate)
          .subtract(1, "days")
          .startOf("day")
          .format(timeFormat);
      }
      return previousDate;
    } else {
      return date;
    }
  };

  // Calculate submittalRequired based on dateRequiredOnSite, leadTime, and approvalTime
  const calculateSubmittalRequired = (
    dateRequiredOnSite,
    leadTime,
    approvalTime
  ) => {
    if (!dateRequiredOnSite || !moment(dateRequiredOnSite).isValid()) {
      return "";
    }

    const leadTimeWeeks = parseInt(leadTime) || 0;
    const approvalTimeWeeks = parseInt(approvalTime) || 0;

    const calculatedDate = moment(dateRequiredOnSite)
      .subtract(leadTimeWeeks, "weeks")
      .subtract(approvalTimeWeeks, "weeks")
      .format(timeFormat);

    // Check if the calculated date is valid (not a holiday or weekend)
    return handlePreviousAvailDate(calculatedDate);
  };

  // Calculate submittalApprovalDue based on dateRequiredOnSite and leadTime

  const calculateSubmittalApprovalDue = (dateRequiredOnSite, leadTime) => {
    if (!dateRequiredOnSite || !moment(dateRequiredOnSite).isValid()) {
      return "";
    }

    const leadTimeWeeks = parseInt(leadTime) || 0;

    const calculatedDate = moment(dateRequiredOnSite)
      .subtract(leadTimeWeeks, "weeks")
      .format(timeFormat);

    // Check if the calculated date is valid (not a holiday or weekend)
    return handlePreviousAvailDate(calculatedDate);
  };

  const notifyParentOfChanges = (updatedData) => {
    if (onChange) {
      onChange(updatedData);
    }
  };

  const handleChange = (field, value) => {
    setData((prev) => {
      const updatedData = {
        ...prev,
        [field]: value,
      };

      // If dateRequiredOnSite or leadTime changes, update submittalRequired and submittalApprovalDue
      if (field === "dateRequiredOnSite" || field === "leadTime") {
        const newDateRequiredOnSite =
          field === "dateRequiredOnSite" ? value : prev.dateRequiredOnSite;
        const newLeadTime = field === "leadTime" ? value : prev.leadTime;

        // Update submittalRequired
        updatedData.submittalRequired = calculateSubmittalRequired(
          newDateRequiredOnSite,
          newLeadTime,
          prev.approvalTime || 0
        );

        // Update submittalApprovalDue
        updatedData.submittalApprovalDue = calculateSubmittalApprovalDue(
          newDateRequiredOnSite,
          newLeadTime
        );
      }
      notifyParentOfChanges(updatedData);
      return updatedData;
    });
    setHasChanges(true);
  };

  const handleDateChange = (field, date) => {
    handleChange(field, date ? date.format("MM-DD-YYYY") : "");
  };

  const parseDate = (dateString) => {
    return dateString && dayjs(dateString).isValid() ? dayjs(dateString) : null;
  };

  const formatDisplayDate = (dateString) => {
    const date = parseDate(dateString);
    return date ? date.format("MM-DD-YYYY") : "-";
  };

  const handleSubmit = () => {
    console.log("Submit for Approval", data);
    // editSubmittal(data);
    setHasChanges(false);
  };

  const isDateDisabled = (date, isSubCommittedDeliveryDate) => {
    // Handling Holidays
    const disabledDatesArray = [];
    Object.values(nonWorkingDays).forEach((holiday) => {
      var currDate = moment(holiday["startDate"], "MM/DD/YYYY").startOf("day");
      var lastDate = moment(holiday["finishDate"], "MM/DD/YYYY").startOf("day");
      disabledDatesArray.push(moment(currDate).format("YYYY-MM-DD"));
      while (currDate.add(1, "days").diff(lastDate) < 0) {
        disabledDatesArray.push(moment(currDate).format("YYYY-MM-DD"));
      }
      disabledDatesArray.push(moment(lastDate).format("YYYY-MM-DD"));
    });

    // Function to check if a date is a weekend or a non-working day based on workingDays object
    const isWeekendOrNonWorkingDay = (dateToCheck) => {
      const dayOfWeek = dateToCheck.day();

      // Map dayOfWeek index to workingDays keys
      const dayName = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ][dayOfWeek];

      const isWeekend = [0, 6].includes(dayOfWeek);
      const isNonWorkingDay = workingDays[dayName] === "0";

      return isWeekend || isNonWorkingDay;
    };

    const isFutureDate = (dateToCheck) => dateToCheck.isAfter(moment(), "day");

    if (isSubCommittedDeliveryDate) {
      return (
        date &&
        (disabledDatesArray.includes(date.format("YYYY-MM-DD")) ||
          isWeekendOrNonWorkingDay(date) ||
          isFutureDate(date))
      );
    } else {
      return (
        date &&
        (disabledDatesArray.includes(date.format("YYYY-MM-DD")) ||
          isWeekendOrNonWorkingDay(date))
      );
    }
  };

  useEffect(() => {
    if (hasChanges) {
      onChange && onChange(data);
    }
    console.log("!!");
  }, [hasChanges]);

  // const scheduleOptions = React.useMemo(() => {
  //   const tempOptions = [
  //     ...scheduleIDsList.map((x) => ({
  //       label: Object.keys(x)[0] + " - " + Object.values(x)[0]["taskName"],
  //       value: Object.keys(x)[0],
  //     })),
  //   ];
  //   const options = tempOptions.sort((a, b) => +a.value - +b.value);

  //   return options;
  // }, [scheduleIDsList]);

  const filterOption = (input, option) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const onSearch = (value) => {
    // console.log("search:", value);
  };

  const handleScheduleChange = (value) => {
    // Prepare updated fields
    let updatedFields = {
      scheduleID: value,
    };

    if (!value) {
      updatedFields.dateRequiredOnSite = "";
    } else if (scheduleIDsList) {
      const findElement = scheduleIDsList.find((obj) => value in obj);
      const formattedElement = findElement?.[value]?.startDate;

      const isValidDate =
        formattedElement && moment(formattedElement).isValid();

      updatedFields.dateRequiredOnSite = isValidDate
        ? moment(formattedElement).format("MM-DD-YYYY")
        : "";
    }

    // Use a single handleChange-like call to update everything at once
    setData((prev) => {
      const merged = {
        ...prev,
        ...updatedFields,
      };

      // If dateRequiredOnSite or leadTime changed, update dependent fields
      const newDateRequiredOnSite =
        updatedFields.dateRequiredOnSite ?? prev.dateRequiredOnSite;
      const newLeadTime = merged.leadTime;

      if (
        updatedFields.dateRequiredOnSite !== undefined ||
        updatedFields.leadTime !== undefined
      ) {
        merged.submittalRequired = calculateSubmittalRequired(
          newDateRequiredOnSite,
          newLeadTime,
          prev.approvalTime || 0
        );

        merged.submittalApprovalDue = calculateSubmittalApprovalDue(
          newDateRequiredOnSite,
          newLeadTime
        );
      }

      notifyParentOfChanges(merged);
      return merged;
    });

    setHasChanges(true);
  };

  const handleDelete = () => {
    onDelete(data.entryID);

    console.log(data.entryID, "deleted??");
  };

  const cancel = (e) => {
    console.log(e);
    // message.error('Click on No');
  };

  return (
    <div className="p-0 d-flex gap-4" style={{ flexWrap: "wrap" }}>
      {/* Left Column */}
      <div style={{ flex: 1, minWidth: 300 }}>
        {deliveryStatus === "Delivered" && (
          <div className="d-flex gap-2 mb-2">
            <Text strong>Succesfully delivered on:</Text>
            <div>{formatDisplayDate(data?.dateRequiredOnSite)}</div>
          </div>
        )}

        <div className="d-flex gap-2 mb-2">
          <Text strong>Submittal Required:</Text>
          <div>{formatDisplayDate(data?.submittalRequired)}</div>
        </div>

        <div className="d-flex gap-2 mb-2">
          <Text strong>Submittal Approval Due:</Text>
          <div>{formatDisplayDate(data?.submittalApprovalDue)}</div>
        </div>
      </div>

      {/* Right Column */}
      <div style={{ flex: 1, minWidth: 300 }}>
        <div className="d-flex justify-content-between align-items-end gap-4 mb-3">
          <div className="w-100 d-flex flex-column">
            <Text strong>Lead Time:</Text>

            <Input
              onKeyDown={(event) => {
                if (
                  !/[0-9]/.test(event.key) &&
                  ![
                    "Backspace",
                    "Delete",
                    "ArrowLeft",
                    "ArrowRight",
                    "Tab",
                  ].includes(event.key)
                ) {
                  event.preventDefault();
                }
              }}
              value={data?.leadTime}
              onChange={(e) => handleChange("leadTime", e.target.value)}
            />
          </div>

          <div className="w-100">
            <Text strong>
              Sub Committed <br /> Delivery Date:
            </Text>

            <DatePicker
              format="MM-DD-YYYY"
              value={parseDate(data?.subCommittedDeliveryDate)}
              onChange={(date) =>
                handleDateChange("subCommittedDeliveryDate", date)
              }
              disabledDate={(date) => isDateDisabled(date, false)}
              style={{ width: "100%" }}
            />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div className="d-flex w-100 justify-content-between align-items-end gap-4 mb-3">
            <div className="w-100 d-flex flex-column">
              <Text strong>Schedule ID:</Text>

              <Select
                // className="w-100"
                style={{ maxWidth: "155px" }}
                showSearch
                placement="bottomLeft"
                placeholder={data.scheduleID || "Select Schedule ID"}
                optionFilterProp="children"
                onChange={handleScheduleChange}
                allowClear={data.scheduleID && data.scheduleID.length > 1}
                onSearch={onSearch}
                filterOption={filterOption}
                options={scheduleOptions}
                value={data.scheduleID}
                dropdownMatchSelectWidth={false}
                dropdownStyle={{ width: "200px" }}
              />
            </div>

            <div className="w-100 d-flex flex-column">
              <Text strong>
                Date Required <br /> On Site:
              </Text>

              <DatePicker
                className="w-100"
                format="MM-DD-YYYY"
                value={parseDate(data?.dateRequiredOnSite)}
                onChange={(date) =>
                  handleDateChange("dateRequiredOnSite", date)
                }
                disabledDate={(date) => isDateDisabled(date, false)}
                style={{ width: "100%" }}
              />
            </div>
          </div>
        </div>

        <div className="w-100 d-flex justify-content-between align-items-end">
          <div className=" d-flex flex-column w-100">
            <Text strong>Contractors:</Text>

            <Select
              className="w-100"
              value={
                data?.assignedUserNames.length > 0
                  ? `${data.assignedUserNames[0]}`
                  : "No Contractors Assigned"
              }
              disabled={data?.assignedUserNames.length < 1 || !data.entryID}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              dropdownMatchSelectWidth={false}
              dropdownStyle={{ width: "200px" }}
            >
              {data?.assignedUserNames.map((name, index) => (
                <Select.Option key={index} value={name}>
                  {name}
                </Select.Option>
              ))}
            </Select>
          </div>

          {/* <Button
            type="primary"
            onClick={handleSubmit}
            disabled={!hasChanges && data.entryID}
          >
            Save
          </Button> */}
        </div>

        {/* Only show delete button for new items */}
        {isNewItem && data?.specTitle && (
          <div className="mt-4">
            <Popconfirm
              title="Delete Submittal"
              description="Are you sure to delete this submittal?"
              onConfirm={handleDelete}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              {/* <Button danger type="primary" onClick={handleDelete}> */}
              <Button danger type="primary">
                <DeleteFilled /> Delete Submittal
              </Button>
            </Popconfirm>
          </div>
        )}
      </div>
    </div>
  );
}

export default Submittal;
